import { QueryResult } from '../types';


type NotebookCell = {
  cell_type: 'code' | 'markdown';
  metadata: Record<string, unknown>;
  source: string[];
  id: string;
  execution_count?: number | null;
  outputs?: unknown[];
};

let cellIdCounter = 0;

function makeMarkdownCell( lines: string[] ): NotebookCell {
  return {
    cell_type: 'markdown',
    metadata: {},
    source: lines.map( ( l, i ) => ( i < lines.length - 1 ? l + '\n' : l ) ),
    id: `cell-${ cellIdCounter++ }`
  };
}

function makeCodeCell( lines: string[] ): NotebookCell {
  return {
    cell_type: 'code',
    metadata: {},
    source: lines.map( ( l, i ) => ( i < lines.length - 1 ? l + '\n' : l ) ),
    execution_count: null,
    outputs: [],
    id: `cell-${ cellIdCounter++ }`
  };
}

export function generateNotebook( sql: string, result: QueryResult ): string {
  cellIdCounter = 0;
  const timestamp = new Date().toISOString();
  const cells: NotebookCell[] = [];

  // Cell 1: markdown header
  cells.push( makeMarkdownCell( [
    '# Data Explorer Query',
    '',
    `Generated: ${ timestamp }`
  ] ) );

  // Cell 2: DuckDB connection boilerplate
  cells.push( makeCodeCell( [
    'import duckdb',
    '',
    'conn = duckdb.connect()'
  ] ) );

  // Cell 3: The SQL query
  const escapedSql = sql.replace( /\\/g, '\\\\' ).replace( /"""/g, '\\"""' );
  cells.push( makeCodeCell( [
    `df = conn.sql("""${ escapedSql }""").df()`
  ] ) );

  // Cell 4: Display the dataframe
  cells.push( makeCodeCell( [ 'df' ] ) );

  // Cell 5: Chart snippet if result has data
  if ( result.rows.length > 0 ) {
    const numericCols = result.columns.filter( col => {
      const sample = result.rows.slice( 0, 20 );
      return sample.some( r => {
        const v = r[col];
        return v !== null && v !== undefined && Number.isFinite( Number( v ) );
      } );
    } );

    if ( numericCols.length > 0 ) {
      const colsStr = numericCols.slice( 0, 4 ).map( c => `'${ c.replace( /\\/g, '\\\\' ).replace( /'/g, '\\\'' ) }'` ).join( ', ' );
      cells.push( makeCodeCell( [
        'import matplotlib.pyplot as plt',
        '',
        `df[[${ colsStr }]].hist(figsize=(10, 6), bins=20)`,
        'plt.tight_layout()',
        'plt.show()'
      ] ) );
    }
  }

  const notebook = {
    nbformat: 4,
    nbformat_minor: 5,
    metadata: {
      kernelspec: {
        display_name: 'Python 3',
        language: 'python',
        name: 'python3'
      },
      language_info: {
        name: 'python',
        version: '3.10.0'
      }
    },
    cells
  };

  return JSON.stringify( notebook, null, 2 );
}
